import React from 'react'
import app from 'firebase/app'
import 'firebase/auth'
import 'firebase/firebase-firestore'
import '@firebase/messaging'
import 'firebase/storage'
import { toast } from 'react-toastify'
import { Icon } from '@material-ui/core'
import fireconfig from '../config.json'

const config = {
	apiKey: fireconfig.apiKey,
	authDomain: fireconfig.authDomain,
	databaseURL: fireconfig.databaseURL,
	projectId: fireconfig.projectId,
	storageBucket: fireconfig.storageBucket,
	messagingSenderId: fireconfig.messagingSenderId,
	appId: fireconfig.appId,
	measurementId: fireconfig.measurementId
}

const firebaseDomain = fireconfig.firebaseDomain

class Firebase {
	constructor() {
		app.initializeApp(config)
		this.auth = app.auth()
		this.db = app.firestore()
		this.storage = app.storage()
		this.googleProvider = new app.auth.GoogleAuthProvider()
		if (app.messaging.isSupported()) {
			this.messaging = app.messaging()
		}
	}

	backupFirestore() {
		const depid = 25
		return this.db.doc(`backup/${depid}`).set({
			id: depid
		}).then(async () => {
			const setRef = await this.db.doc(`backupinprogress/${depid}`)
			if (!setRef.exists) {
				this.db.doc(`backupinprogress/${depid}`).set({
					active: true
				})
			} else {
				setRef.delete()
					.then(() => {
						this.db.doc(`backupinprogress/${depid}`).set({
							active: true
						})
					})
			}
		})
	}

	async restoreFirestore(url, backupdate) {
		const depid = 25
		const date = parseInt(backupdate)
		const setRef = await this.db.doc(`restore/${depid}`)
		if (!setRef.exists) {
			return this.db.doc(`restore/${depid}`).set({
				url: url,
				backupDate: date
			}).then(() => {
				this.db.doc(`restoreinprogress/${depid}`).set({
					active: true
				})
			})
		} else {
			setRef.delete()
				.then(() => {
					return this.db.doc(`restore/${depid}`).set({
						url: url,
						backupDate: date
					}).then(() => {
						this.db.doc(`restoreinprogress/${depid}`).set({
							active: true
						})
					})
				})
		}
	}

	async restoreHistory(url, restoredate, backupdate) {
		const depid = 25
		const date = parseInt(restoredate)
		const backdate = parseInt(backupdate)
		const setRef = await this.db.doc(`restorehistory/${depid}`)
		if (!setRef.exists) {
			return this.db.doc(`restorehistory/${depid}`).set({
				url: url,
				restoreDate: date,
				backupDate: backdate
			}).then(() => {
				this.db.doc(`restoreinprogress/${depid}`).set({
					active: true
				})
			}).catch((error) => {
				console.log(error)
			})
		} else {
			setRef.delete()
				.then(() => {
					return this.db.doc(`restorehistory/${depid}`).set({
						url: url,
						restoreDate: date,
						backupDate: backdate
					}).then(() => {
						this.db.doc(`restoreinprogress/${depid}`).set({
							active: true
						})
					}).catch((error) => {
						console.log(error)
					})
				})
		}
	}

	async restoreAutoHistory(url, restoredate, backupdate) {
		const depid = 25
		const date = parseInt(restoredate)
		const backdate = parseInt(backupdate)
		const setRef = await this.db.doc(`restoreautohistory/${depid}`)
		if (!setRef.exists) {
			return this.db.doc(`restoreautohistory/${depid}`).set({
				url: url,
				restoreDate: date,
				backupDate: backdate
			}).then(() => {
				this.db.doc(`restoreinprogress/${depid}`).set({
					active: true
				})
			}).catch((error) => {
				console.log(error)
			})
		} else {
			setRef.delete()
				.then(() => {
					return this.db.doc(`restoreautohistory/${depid}`).set({
						url: url,
						restoreDate: date,
						backupDate: backdate
					}).then(() => {
						this.db.doc(`restoreinprogress/${depid}`).set({
							active: true
						})
					}).catch((error) => {
						console.log(error)
					})
				})
		}
	}

	async getBackuplist() {
		const backups = []
		await this.db.collection('backuplist').get().then((querySnapshot) => {
			querySnapshot.forEach(doc => {
				backups.push(doc.data())
			})
		})
		return backups
	}

	async getAutoBackuplist() {
		const autobackups = []
		await this.db.collection('autobackuplist').get().then((querySnapshot) => {
			querySnapshot.forEach(doc => {
				autobackups.push(doc.data())
			})
		})
		return autobackups
	}

	login(email, password) {
		return this.auth.signInWithEmailAndPassword(email, password)
	}

	logout() {
		return this.auth.signOut()
	}

	isInitialized() {
		return new Promise(resolve => {
			this.auth.onAuthStateChanged(resolve)
		})
	}

	async register(name, email, password) {
		await this.auth.createUserWithEmailAndPassword(email, password)
			.then(() => {
				this.auth.currentUser.updateProfile({
					displayName: name
				})
			})
			.then(() => {
				setTimeout(() => {
					return
				}, 1000)
			})
	}

	async forgotPassword(email) {
		await this.auth.sendPasswordResetEmail(email)
	}

	resetPassword(currentpassword, newpassword) {
		var user = this.auth.currentUser
		var cred = app.auth.EmailAuthProvider.credential(user.email, currentpassword)
		var newPassword = newpassword
		user.reauthenticateWithCredential(cred)
			.then(() => {
				user.updatePassword(newPassword).then(() => {
					this.logout().then(() => {
						window.location.href = '/'
					})
				})
				toast.success(<Icon>done_outline</Icon>)
			}).catch((error) => {
				toast.error('Wrong password!', {
					autoClose: 1000,
					hideProgressBar: true
				})
			})
	}

	doSendEmailVerification = () =>
		this.auth.currentUser.sendEmailVerification({
			url: firebaseDomain,
		})

	getCurrentUser() {
		return this.auth.currentUser
	}

	checkClaims() {
		return this.auth.currentUser.getIdTokenResult()
	}

	getCurrentUsername() {
		return this.auth.currentUser && this.auth.currentUser.displayName
	}

	getCurrentUserPhoto() {
		return this.auth.currentUser && this.auth.currentUser.photoURL
	}

	async getCurrentUserEmail() {
		return this.auth.currentUser && this.auth.currentUser.email
	}

	async getAdmin() {
		const admins = []
		await this.db.collection('backupadmin')
			.get()
			.then((querySnapshot) => {
				querySnapshot.forEach(doc => {
					admins.push(doc.data())
				})
			})
		return admins
	}

	makeAdmin(userId, name, email) {
		return this.db.doc(`backupadmin/${userId}`).set({
			name, email, id: userId
		})
	}

	removeAdmin(userId) {
		return this.db.doc(`backupadmin/${userId}`).delete()
	}

	addSettings(
		language,
		apptitle,
		logo,
		bgimage
	) {
		const setRef = this.db.collection('backupsettings').doc('settings')
		if (setRef.exists) {
			return this.db.collection('backupsettings').doc('settings').update({
				language,
				apptitle,
				logo,
				bgimage
			})
		} else {
			return this.db.collection('backupsettings').doc('settings').set({
				language,
				apptitle,
				logo,
				bgimage
			})
		}
	}

	changeLogo() {
		const logoRef = this.db.collection('backupsettings').doc('settings')
		return logoRef.get()
			.then((doc) => {
				const logo = (doc.data().logo)
				return logo
			})
			.then(logo => {
				if (logo !== '') {
					var storage = this.storage
					var storageRef = storage.ref()
					var logoUrl = storage.refFromURL(logo)
					logoUrl.getMetadata().then((metadata) => {
						var logoName = storageRef.child(`backuplogo/${metadata.name}`)
						logoName.delete()
					}).catch((error) => {
						console.log(error)
						return
					})
				} else {
					return null
				}
			})
	}

	changeBGImage() {
		const imageRef = this.db.collection('backupsettings').doc('settings')
		return imageRef.get()
			.then((doc) => {
				const bgimage = (doc.data().bgimage)
				return bgimage
			})
			.then(bgimage => {
				if (bgimage !== '') {
					var storage = this.storage
					var storageRef = storage.ref()
					var imageUrl = storage.refFromURL(bgimage)
					imageUrl.getMetadata().then((metadata) => {
						var imageName = storageRef.child(`backupbackground/${metadata.name}`)
						imageName.delete()
					}).catch((error) => {
						console.log(error)
						return
					})
				} else {
					return null
				}
			})
	}

}

export default new Firebase()